import { useState } from 'react';
import emailjs from '@emailjs/browser';
import './orzur.css';

const EMAILJS_SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID || '';
const EMAILJS_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID || '';
const EMAILJS_PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY || '';

const LOGO_SRC = '/orzur-logo-white.png';

const NAV_LINKS = [
  { label: 'Home', href: '#home' },
  { label: 'Platform', href: '#platform' },
  { label: 'Use Cases', href: '#use-cases' },
  { label: 'Outcomes', href: '#outcomes' },
  { label: 'About', href: '#about' },
  { label: 'Contact', href: '#contact' },
];

const ABOUT_PARAGRAPHS = [
  "Manufacturing leaders are stuck in fragmented, manual work. Data lives in six systems. Answers take three days. Decisions run on last week's numbers. The gap between what leaders know and what they need to know is growing every quarter.",
  "We saw this firsthand - on factory floors, in production meetings, in late-night spreadsheet sessions that shouldn't have been necessary. The data existed. The visibility didn't.",
  "Orzur closes that gap. It isn't an upgrade to your dashboards. It's a rebuild from first principles - a platform that unifies scattered manufacturing data and makes it answerable through conversation. Built by a team that has operated inside plants, not just studied them.",
  "Our mission is simple: make manufacturing leaders superhuman. Give them instant access to the answers locked inside their own systems - so they can stop waiting for reports and start making decisions.",
];

const ABOUT_VALUES = [
  {
    title: 'Built on the floor, not in a lab',
    body: 'Every feature was shaped by real conversations with plant heads, operations directors, and manufacturing CFOs. We build what operators actually need - not what looks good in a pitch deck.',
  },
  {
    title: 'Speed is a feature',
    body: "Three-day turnarounds for cross-plant metrics aren't acceptable when margins are eroding today. Orzur is built for answers in seconds - because manufacturing doesn't wait.",
  },
  {
    title: 'Trust is non-negotiable',
    body: "Every answer shows its source. Every query is auditable. Your data stays in your network. We don't ask manufacturers to trust us - we let them verify everything.",
  },
];

const SECURITY_CARDS = [
  {
    title: 'On-Premises Deployment',
    body: 'Deploy Orzur entirely within your data center or private cloud. Your production data, query logs, and analytics never leave your network. No external API calls, no cloud dependencies - full air-gap capability.',
  },
  {
    title: 'Data Lineage & Auditability',
    body: 'Every answer Orzur returns includes full source attribution - which system, which table, which time range. Every query is logged with user identity, timestamp, and the SQL generated. Complete audit trail for compliance.',
  },
  {
    title: 'Role-Based Access Control',
    body: "Fine-grained permissions ensure each user sees only what they should. A shift supervisor sees their line's data. A plant head sees their facility. A VP sees across plants. Access boundaries are enforced at the data layer.",
  },
  {
    title: 'Encryption & Infrastructure',
    body: 'Data encrypted at rest (AES-256) and in transit (TLS 1.3). Read-only database access - Orzur queries your systems but never modifies them. No schema changes, no write operations, no risk to production databases.',
  },
  {
    title: 'Responsible AI',
    body: 'AI models run locally within your deployment - no data sent to external LLM providers. Deterministic query processing for consistent, reproducible results. Full transparency into how queries are interpreted and executed.',
  },
];


const HERO_PILLS = ['ASK', 'ANALYZE', 'DECIDE', 'TRUST'];

const PROBLEM_SOURCES = ['ERP', 'MES', 'CRM', 'SCADA', 'Spreadsheets', 'WhatsApp', 'Paper Forms', 'Email'];

const PROBLEM_CARDS = [
  {
    num: '01',
    title: 'Fragmented systems, fragmented truth',
    body: 'Data lives in six or more disconnected systems. No single source of truth exists across the organization. Every department works from a different version of reality.',
    detail: 'Production sees one number. Finance sees another. The plant head sees a third - and all of them are correct within their own silo.',
  },
  {
    num: '02',
    title: 'Reports that arrive too late',
    body: "Leadership waits days for numbers that should take seconds. By the time insights arrive, the window to act has closed. Shift decisions rely on yesterday's snapshot.",
    detail: 'A quality deviation at 10am becomes a P&L line item by the time anyone sees the data at end-of-week review.',
  },
  {
    num: '03',
    title: 'Tribal knowledge at risk',
    body: "Critical operational knowledge lives in people's heads, not in systems. When experienced operators leave, institutional knowledge walks out the door with them.",
    detail: 'The plant veteran who "just knows" why Line 3 runs 12% slower on Mondays retires - and that insight vanishes forever.',
  },
];

const COST_CARDS = [
  { big: '3 Days', label: 'Average time to get a cross-plant metric - manually assembled from separate systems' },
  { big: '6+ Systems', label: 'With production data that never talks to each other - each requiring its own export-and-stitch workflow' },
  { big: '<1%', label: 'Of manufacturing data is ever analyzed - the rest sits in databases no one queries' },
  { big: '72 hrs', label: 'Average delay before leadership sees a quality deviation that impacts margin' },
];

const REBUILD_PAIRS = [
  {
    not: 'Not another BI tool.',
    yes: 'A platform that unifies every data source and makes it answerable - in seconds.',
    detail: 'Orzur connects to your ERP, MES, SCADA, and spreadsheets. You ask a question in plain English. It queries across all systems and returns the answer with source lineage.',
  },
  {
    not: 'Not a chatbot bolted on.',
    yes: 'Conversational AI that understands manufacturing - natively.',
    detail: 'Suppliers, inventory levels, capacity utilization, cost-per-unit, OEE, scrap rates - Orzur speaks your language out of the box. No months of training or customization required.',
  },
  {
    not: 'Not a six-month integration.',
    yes: 'Connects to your existing systems in weeks. Not quarters.',
    detail: "Pre-built connectors for SAP, Oracle, Microsoft Dynamics, and common MES platforms. Your IT team isn't building a data warehouse - they're pointing Orzur at what already exists.",
  },
  {
    not: "Not another tool your team won't use.",
    yes: 'An interface anyone can use - from the plant head to the CEO.',
    detail: 'If you can type a question, you can use Orzur. No SQL. No analyst. No training. The same platform works for the operator checking shift performance and the CFO reviewing quarterly margins.',
  },
];

type UseCase = {
  id: string;
  navTitle: string;
  eyebrow: string;
  title: string;
  body: string;
  questions: string[];
  stat: string;
  note: string;
};

const USE_CASES: UseCase[] = [
  {
    id: 'supply',
    navTitle: 'Supply Chain Visibility',
    eyebrow: 'Supply Chain Visibility',
    title: 'Know which suppliers are at risk before they cause a line stoppage.',
    body: "Track on-time delivery, defect rates, and lead time variance across all vendors - from a single conversational query. Orzur pulls data from your ERP's purchase orders, goods receipts, and quality inspection records to surface supplier risk instantly.",
    questions: [
      '"Which suppliers missed delivery targets this quarter?"',
      '"Show me rejection rates by vendor for the last 6 months."',
      '"Compare lead time variance for our top 10 suppliers."',
    ],
    stat: 'Supplier risk surfaced in seconds - not discovered during the next quarterly business review.',
    note: 'Procurement teams shift from reactive firefighting to proactive vendor management.',
  },
  {
    id: 'inventory',
    navTitle: 'Inventory Intelligence',
    eyebrow: 'Inventory Intelligence',
    title: 'Spot overstock and shortfalls across SKUs and plants - before they hit the line.',
    body: 'Match real-time inventory levels to incoming orders and production schedules. Orzur connects your warehouse management data, production plans, and sales orders to give you a unified view of what you have, what you need, and where the gaps are.',
    questions: [
      '"Which SKUs are below safety stock across all plants?"',
      '"Show me dead inventory - items with zero movement in 90 days."',
      '"Do we have enough raw material to fulfill next week\'s orders?"',
    ],
    stat: 'Cross-plant inventory matched to production demand - instantly.',
    note: 'Reduce carrying costs and prevent line stoppages from material shortages.',
  },
  {
    id: 'margin',
    navTitle: 'Margin Protection',
    eyebrow: 'Margin Protection',
    title: 'Catch margin erosion before it shows up in the P&L.',
    body: 'Track cost-per-unit trends across plants, shifts, and product lines. Orzur pulls cost data from your ERP - material costs, labor allocation, overhead rates - and correlates it with production output to surface margin trends that would otherwise take weeks to compile.',
    questions: [
      '"Show me cost-per-unit trend for Product Line A across all plants."',
      '"Which plant has the highest scrap cost this month?"',
      '"Compare material cost variance between Q1 and Q2."',
    ],
    stat: 'Cost trends surfaced in seconds - replacing multi-day report assembly.',
    note: 'Finance and operations teams align on margin drivers without waiting for month-end close.',
  },
  {
    id: 'benchmark',
    navTitle: 'Cross-Plant Benchmarking',
    eyebrow: 'Cross-Plant Benchmarking',
    title: 'Compare any metric across any plant - without assembling a slide deck.',
    body: 'Eliminate the monthly ritual of collecting data from each plant, normalizing it in spreadsheets, and building comparison charts. Orzur gives you instant, apples-to-apples benchmarking across locations with consistent KPI definitions.',
    questions: [
      '"Compare OEE across all plants for the last 3 months."',
      '"Which plant has the highest downtime this week?"',
      '"Show production efficiency by shift across Plant A and Plant B."',
    ],
    stat: 'Any KPI, any plant, any timeframe - one question.',
    note: 'Operations leaders identify best practices and underperformance in real time, not at quarterly reviews.',
  },
  {
    id: 'quality',
    navTitle: 'Quality Analytics',
    eyebrow: 'Quality Analytics',
    title: 'Move from reactive quality firefighting to proactive defect prevention.',
    body: 'Analyze rejection rates, defect categories, and quality trends across machines, shifts, and product lines. Orzur correlates quality data with production parameters to help you identify root causes - not just symptoms.',
    questions: [
      '"What are the top 5 rejection reasons this month?"',
      '"Show me defect rate by machine for the night shift."',
      '"Which product line has the highest scrap rate trend?"',
    ],
    stat: 'Quality deviations caught in hours, not discovered at end-of-week review.',
    note: 'Reduce scrap costs and prevent quality issues from propagating across shifts.',
  },
  {
    id: 'production',
    navTitle: 'Production Intelligence',
    eyebrow: 'Production Intelligence',
    title: "See what's happening on the floor right now - not what happened yesterday.",
    body: 'Monitor production targets vs. actuals, machine utilization, downtime reasons, and shift performance in real time. Orzur turns your hourly production data into actionable intelligence that anyone in the organization can query.',
    questions: [
      '"Are we on track for today\'s production target?"',
      '"Show me machine utilization for the current shift."',
      '"What caused downtime on CNC-003 this week?"',
    ],
    stat: 'Shift supervisors and plant heads operating from the same real-time data.',
    note: "Decisions happen during the shift - not at the next morning's review meeting.",
  },
];

const PILLAR_CARDS = [
  {
    name: 'Ask',
    title: 'Ask any question in plain language',
    body: "No SQL. No analyst. No waiting. Type your question the way you'd ask a colleague - Orzur queries across all connected systems and returns the answer in seconds.",
    features: [
      'Natural language queries across all connected data sources',
      'Understands manufacturing terminology natively - OEE, scrap rate, cycle time',
      'Follow-up questions that build on previous context',
      'Works for operators, plant heads, and C-suite equally',
    ],
    outcome: '"Which suppliers missed targets this quarter?" - answered in 8 seconds, pulling from ERP purchase orders and goods receipt data.',
  },
  {
    name: 'Analyze',
    title: 'Cross-plant analytics from a unified layer',
    body: "Orzur doesn't just query one system - it correlates data across your ERP, MES, quality systems, and production databases to surface insights no single system can provide.",
    features: [
      'Cross-system data correlation without manual joins',
      'Multi-plant comparisons with consistent KPI definitions',
      'Trend analysis across time periods, shifts, and product lines',
      'Drill-down from overview to machine-level detail',
    ],
    outcome: '6 months of production data across 3 plants - queried and compared in a single conversation.',
  },
  {
    name: 'Decide',
    title: 'Real-time insights replace stale reports',
    body: "Stop making decisions based on last week's data. Orzur gives you the numbers you need when you need them - during the shift, during the meeting, during the call with procurement.",
    features: [
      'Live production monitoring with target vs. actual tracking',
      'Proactive alerts when metrics breach thresholds',
      'Automated KPI dashboards that refresh continuously',
      'Decision context - not just the what, but the why',
    ],
    outcome: "Margin erosion caught in real time - not discovered in next month's P&L review.",
  },
  {
    name: 'Trust',
    title: 'Trust your data - every answer shows its source',
    body: 'Every answer Orzur returns comes with full source lineage - which system, which table, which time range. Your data never leaves your network unless you choose.',
    features: [
      'Full data lineage - see exactly where every number came from',
      'On-premises deployment - your data stays in your data center',
      'Role-based access - different visibility for different roles',
      'Audit trail for every query and every answer',
    ],
    outcome: 'Full lineage. On-prem deployment. Zero data exposure. Enterprise-grade security from day one.',
  },
];

const INTEGRATION_CATEGORIES = [
  { name: 'ERP Systems', items: 'SAP, Oracle, Microsoft Dynamics, Tally, and custom ERP platforms via API connectors' },
  { name: 'Manufacturing Execution', items: 'MES platforms, SCADA systems, PLC data feeds, and IoT sensor streams' },
  { name: 'Quality & Compliance', items: 'QMS systems, inspection data, lab results, and regulatory compliance platforms' },
  { name: 'Operational Data', items: 'Spreadsheets, CSV exports, internal databases, email reports, and manual log digitization' },
];

const TESTIMONIALS = [
  {
    quote: "For the first time, I can ask a question about last week's production and get an answer in seconds - not wait three days for someone to pull it from SAP. That changes how I run the business.",
    initials: 'VP',
    name: 'VP of Operations',
    role: 'Multi-plant automotive manufacturer',
    featured: true,
  },
  {
    quote: 'We used to spend two days every month building cross-plant comparison decks. Now anyone on my team can get that data in a single query. The time savings alone justified the investment.',
    initials: 'PH',
    name: 'Plant Head',
    role: 'Industrial components manufacturer',
  },
  {
    quote: "What surprised me wasn't the AI - it was that it actually understood our manufacturing context. I asked about OEE by shift and got a meaningful answer on the first try. No training, no customization.",
    initials: 'DO',
    name: 'Director of Operations',
    role: 'Government regulated Manufacturer',
  },
];

const OUTCOMES = [
  { title: 'Supply Chain Visibility', body: 'Know which suppliers are at risk before they cause a line stoppage. Track on-time delivery across all vendors - in one question.', metric: 'Supplier risk surfaced in seconds - not discovered at quarterly review.' },
  { title: 'Inventory Intelligence', body: 'Spot overstock and shortfalls across SKUs and plants. Match inventory to incoming orders in real time.', metric: 'Cross-plant inventory matched to demand - instantly.' },
  { title: 'Margin Protection', body: 'Track cost-per-unit trends across plants. Catch margin erosion before it shows up in the P&L.', metric: 'Cost trends surfaced in seconds - replacing multi-day reports.' },
  { title: 'Cross-Plant Benchmarking', body: 'Compare any metric across any plant. No more monthly slide decks assembled by hand.', metric: 'Any KPI, any plant, any timeframe - one query.' },
  { title: 'Quality Acceleration', body: 'Identify rejection patterns, correlate defects with production parameters, and surface root causes before they propagate.', metric: 'Quality deviations caught in hours, not at end-of-week review.' },
  { title: 'Leadership Alignment', body: 'Every leader - from plant head to CEO - operates from the same data, the same definitions, and the same real-time view of the business.', metric: 'One version of the truth - across every level and every plant.' },
];

const WHY_CARDS = [
  {
    title: 'Your data stays yours.',
    body: "Deploys on-premises or in your private cloud. Nothing leaves your network unless you choose. Most AI platforms can't even offer this.",
    detail: 'Enterprise-grade security: SOC 2 ready, role-based access, full audit trails, encrypted at rest and in transit.',
  },
  {
    title: 'Speaks manufacturing - natively.',
    body: 'Understands suppliers, inventory, cost structures, capacity, OEE, scrap rates, and production KPIs out of the box. No months of customization.',
    detail: 'Pre-built knowledge of manufacturing terminology, unit conversions, shift patterns, and industry-standard metrics.',
  },
  {
    title: 'Built by people who know plants.',
    body: 'Our team has operated inside manufacturing environments. We built this because we felt the problem - not because we read about it in a market report.',
    detail: 'Every feature was shaped by conversations with plant heads, operations directors, and manufacturing CFOs.',
  },
];

const CTA_CARDS = [
  { title: 'Free 90-Day Pilot', body: 'One production line. Hosted on our infrastructure. Zero burden on your IT team. See results before you commit.' },
  { title: 'Production in Weeks', body: 'Deploy on your data center or cloud. SaaS or on-prem - same platform, same price. No data warehouse project required.' },
  { title: 'Scale Across Plants', body: 'Multi-plant rollout with dedicated support. Add locations, add data sources, add users - a platform that grows with your operations.' },
];

function Nav() {
  return (
    <nav>
      <div className="nav-inner">
        <a className="nav-brand" href="#home">
          <img src={LOGO_SRC} alt="Orzur" className="nav-logo-img" />
          <span className="nav-tagline">Manufacturing Productivity Platform</span>
        </a>
        <div className="nav-links">
          {NAV_LINKS.map((l) => (
            <a key={l.label} href={l.href}>{l.label}</a>
          ))}
          <a href="#contact" className="nav-cta">Get a Demo</a>
        </div>
      </div>
    </nav>
  );
}

function Hero() {
  return (
    <section className="hero" id="home">
      <div className="hero-inner">
        <h1>The era of report-based decisions <em>is&nbsp;over.</em></h1>
        <h2>Orzur unifies your plant data and makes it answerable - in seconds, not days. From scattered data to trusted decisions.</h2>
        <div className="hero-pillars">
          {HERO_PILLS.map((p) => (
            <span key={p} className="hero-pill">{p}</span>
          ))}
        </div>
        <div className="hero-ctas">
          <a href="#contact" className="btn-primary">Get a Demo <span>&rarr;</span></a>
          <a href="#platform" className="btn-secondary">See How It Works</a>
        </div>
      </div>
    </section>
  );
}

function Manifesto() {
  return (
    <section className="manifesto">
      <div className="manifesto-inner">
        <div className="manifesto-label">Our Manifesto</div>
        <div className="manifesto-lines">
          <div className="manifesto-line muted">Manufacturing leaders are stuck in fragmented, manual work.</div>
          <div className="manifesto-line muted">Data lives in six systems. Answers take three days. Decisions run on last week's numbers.</div>
          <div className="manifesto-line">The gap between what leaders know and what they need to know is growing every quarter.</div>
          <div className="manifesto-line accent">Orzur closes that gap.</div>
          <div className="manifesto-line bold">It isn't an upgrade to your dashboards. It's a rebuild from first principles.</div>
        </div>
      </div>
    </section>
  );
}

function Problem() {
  return (
    <section className="problem" id="problem">
      <div className="section-inner">
        <div className="section-eyebrow">The Problem</div>
        <div className="section-title">Your data exists.<br />Your visibility doesn't.</div>
        <div className="section-subtitle">
          Manufacturing runs on data - but less than 1% of it is ever analyzed. The rest sits trapped across disconnected systems, waiting for someone to pull it manually.
        </div>
        <div className="problem-sources">
          {PROBLEM_SOURCES.map((s) => (
            <span key={s} className="source-pill">{s}</span>
          ))}
        </div>
        <div className="problem-grid">
          {PROBLEM_CARDS.map((c) => (
            <div key={c.num} className="problem-card">
              <span className="problem-num">{c.num}</span>
              <h3>{c.title}</h3>
              <p>{c.body}</p>
              <div className="problem-detail">{c.detail}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Cost() {
  return (
    <section className="cost">
      <div className="section-inner">
        <div className="section-eyebrow" style={{ color: 'var(--purple-light)' }}>The Cost</div>
        <div className="section-title">The price of not knowing.</div>
        <div className="section-subtitle">
          Every day of delayed visibility compounds into lost margin, missed targets, and reactive firefighting.
        </div>
        <div className="cost-layout">
          <div>
            <div className="cost-stat">
              <div className="cost-big">15 - 20%</div>
              <div className="cost-label">of manufacturing revenue lost to poor quality - annually.</div>
              <div className="cost-note">
                Most of it preventable - if the data had been caught in time. The problem isn't absence of data. It's absence of access.
              </div>
            </div>
          </div>
          <div className="cost-grid">
            {COST_CARDS.map((c) => (
              <div key={c.big} className="cost-card">
                <div className="cost-card-big">{c.big}</div>
                <div className="cost-card-label">{c.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function Rebuild() {
  return (
    <section className="rebuild" id="platform">
      <div className="section-inner">
        <div className="section-title">
          We didn't add AI to a dashboard.<br />
          <span className="rebuild-highlight">We rebuilt how manufacturers get answers.</span>
        </div>
        <div className="rebuild-pairs">
          {REBUILD_PAIRS.map((p) => (
            <div key={p.not} className="rebuild-row">
              <span className="rebuild-not">{p.not}</span>
              <span className="rebuild-dot" />
              <div className="rebuild-right">
                <div className="rebuild-yes">{p.yes}</div>
                <div className="rebuild-detail">{p.detail}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function UseCasesSection() {
  const [active, setActive] = useState(USE_CASES[0].id);
  const current = USE_CASES.find((u) => u.id === active)!;

  return (
    <section className="use-cases" id="use-cases">
      <div className="section-inner">
        <div className="section-eyebrow">Use Cases</div>
        <div className="section-title">Built for human leaders - to make them superhuman.</div>
        <div className="section-subtitle">Select a use case to see how Orzur delivers real-world outcomes across your operations.</div>
        <div className="uc-layout">
          <div className="uc-nav">
            {USE_CASES.map((u) => (
              <button
                key={u.id}
                className={`uc-nav-item${u.id === active ? ' active' : ''}`}
                onClick={() => setActive(u.id)}
              >
                <div className="uc-nav-title">{u.navTitle}</div>
              </button>
            ))}
          </div>

          <div className="uc-detail active">
            <div className="uc-detail-eyebrow">{current.eyebrow}</div>
            <h3>{current.title}</h3>
            <div className="uc-detail-body">{current.body}</div>
            <div className="uc-detail-questions">
              <h4>Questions you can ask</h4>
              {current.questions.map((q, i) => (
                <div key={i} className="uc-question">{q}</div>
              ))}
            </div>
            <div className="uc-outcome-box">
              <div className="uc-outcome-label">Real-World Outcome</div>
              <div className="uc-outcome-stat">{current.stat}</div>
              <div className="uc-outcome-note">{current.note}</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Pillars() {
  return (
    <section className="pillars" id="pillars">
      <div className="section-inner">
        <div className="section-eyebrow">How It Works</div>
        <div className="section-title">Four pillars. One platform.</div>
        <div className="section-subtitle">From scattered data to trusted decisions - in seconds, not days.</div>
        <div className="pillar-deep">
          {PILLAR_CARDS.map((p) => (
            <div key={p.name} className="pillar-card">
              <div className="pillar-accent" />
              <div className="pillar-name">{p.name}</div>
              <h3>{p.title}</h3>
              <p>{p.body}</p>
              <div className="pillar-features">
                {p.features.map((f, i) => (
                  <div key={i} className="pillar-feature">
                    <span className="pillar-feature-dot" /> {f}
                  </div>
                ))}
              </div>
              <div className="pillar-outcome">{p.outcome}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Integrations() {
  return (
    <section className="integrations" id="integrations">
      <div className="section-inner">
        <div className="section-eyebrow" style={{ color: 'var(--purple-light)' }}>Integrations</div>
        <div className="section-title">Connects to the systems you already run.</div>
        <div className="section-subtitle">No rip-and-replace. No data warehouse project. Orzur sits on top of your existing infrastructure.</div>
        <div className="int-layout">
          <div className="int-left">
            <p>
              Orzur connects to your existing systems through pre-built connectors and standard APIs. Your IT team doesn't need to build a data lake - they point Orzur at what already exists, and it handles the rest.
            </p>
            <div className="int-categories">
              {INTEGRATION_CATEGORIES.map((c) => (
                <div key={c.name} className="int-cat">
                  <div className="int-cat-name">{c.name}</div>
                  <div className="int-cat-items">{c.items}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="int-right">
            <div className="int-stat">
              <div className="int-stat-big">Weeks</div>
              <div className="int-stat-label">Typical integration timeline - not months, not quarters</div>
            </div>
            <div className="int-stat">
              <div className="int-stat-big">Zero</div>
              <div className="int-stat-label">Data warehouse projects required - connects to your existing databases directly</div>
            </div>
            <div className="int-deploy">
              <h4>Deployment flexibility</h4>
              <p>On-premises, private cloud, or SaaS - same platform, same capabilities. Your data stays exactly where your security team wants it.</p>
            </div>
            <div className="int-deploy emerald">
              <h4>Minimal IT burden</h4>
              <p>Read-only access to existing databases. No schema changes. No new infrastructure to maintain. Your IT team's involvement is measured in hours, not headcount.</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Testimonials() {
  return (
    <section className="testimonials" id="testimonials">
      <div className="section-inner">
        <div className="section-eyebrow">What Leaders Say</div>
        <div className="section-title">Trusted by manufacturing leaders who've seen the difference.</div>
        <div className="section-subtitle" />
        <div className="testimonial-grid">
          {TESTIMONIALS.map((t, i) => (
            <div key={i} className={`testimonial-card${t.featured ? ' featured' : ''}`}>
              <div className="testimonial-quote">{t.quote}</div>
              <div className="testimonial-author">
                <div className="testimonial-avatar">{t.initials}</div>
                <div>
                  <div className="testimonial-name">{t.name}</div>
                  <div className="testimonial-role">{t.role}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Outcomes() {
  return (
    <section className="outcomes" id="outcomes">
      <div className="section-inner">
        <div className="section-eyebrow">Real-World Outcomes</div>
        <div className="section-title">What changes in the first 90 days.</div>
        <div className="section-subtitle">Measurable improvements that compound over time - not theoretical ROI slides.</div>
        <div className="outcome-grid">
          {OUTCOMES.map((o) => (
            <div key={o.title} className="outcome-card">
              <div className="outcome-accent" />
              <h3>{o.title}</h3>
              <p>{o.body}</p>
              <div className="outcome-metric">{o.metric}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Why() {
  return (
    <section className="why" id="why-orzur">
      <div className="section-inner">
        <div className="section-eyebrow" style={{ color: 'var(--purple-light)' }}>Why Orzur</div>
        <div className="section-title">Built for manufacturing.<br />Not retrofitted to it.</div>
        <div style={{ height: 48 }} />
        <div className="why-grid">
          {WHY_CARDS.map((w) => (
            <div key={w.title} className="why-card">
              <h3>{w.title}</h3>
              <p>{w.body}</p>
              <div className="why-detail">{w.detail}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function About() {
  return (
    <section className="about" id="about">
      <div className="section-inner">
        <div className="section-eyebrow">About Orzur</div>
        <div className="section-title">We built this because we felt the problem.</div>
        <div className="section-subtitle">Orzur was born inside manufacturing - not from a whiteboard in a tech office.</div>
        <div className="about-layout">
          <div className="about-text">
            {ABOUT_PARAGRAPHS.map((p, i) => <p key={i}>{p}</p>)}
          </div>
          <div className="about-values">
            {ABOUT_VALUES.map((v) => (
              <div key={v.title} className="about-value">
                <h4>{v.title}</h4>
                <p>{v.body}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function Security() {
  return (
    <section className="security" id="security">
      <div className="section-inner">
        <div className="section-eyebrow">Security &amp; Trust</div>
        <div className="section-title">Enterprise-grade security. By default, not as an add-on.</div>
        <div className="section-subtitle">
          Manufacturing data is sensitive. Orzur is built from the ground up to protect it - not retrofitted with security as an afterthought.
        </div>
        <div className="security-grid">
          {SECURITY_CARDS.map((c) => (
            <div key={c.title} className="security-card">
              <h3>{c.title}</h3>
              <p>{c.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Careers() {
  return (
    <section className="careers" id="careers">
      <div className="section-inner">
        <div className="section-eyebrow" style={{ color: 'var(--purple-light)' }}>Careers</div>
        <div className="section-title">Building the future of manufacturing intelligence.</div>
        <div className="careers-message">
          We're a small, focused team solving a hard problem - turning fragmented manufacturing data into instant, trusted answers. We move fast, ship often, and care deeply about the people who use what we build.
        </div>
        <div className="careers-note">
          <h4>No open positions right now</h4>
          <p>
            We don't have any openings at the moment, but we're always interested in hearing from exceptional people who are passionate about manufacturing, AI, and building products that matter. If that sounds like you, drop us a note at{' '}
            <a href="mailto:admin@orzur.ai">admin@orzur.ai</a> - we'll keep your details on file.
          </p>
        </div>
      </div>
    </section>
  );
}

type ContactFormState = {
  name: string;
  email: string;
  company: string;
  interest: string;
  message: string;
};

const INITIAL_FORM: ContactFormState = {
  name: '',
  email: '',
  company: '',
  interest: '',
  message: '',
};

function ContactForm() {
  const [data, setData] = useState<ContactFormState>(INITIAL_FORM);
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const update = (k: keyof ContactFormState) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => setData((prev) => ({ ...prev, [k]: e.target.value }));

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (status === 'sending') return;

    setStatus('sending');
    setErrorMsg('');

    const templateParams = {
      from_name: data.name,
      from_email: data.email,
      reply_to: data.email,
      company: data.company,
      subject: data.interest ? `Orzur enquiry - ${data.interest}` : 'Orzur enquiry',
      interest: data.interest || 'Not specified',
      message: data.message || '(no message provided)',
      to_email: 'admin@orzur.ai',
    };

    try {
      if (!EMAILJS_SERVICE_ID || !EMAILJS_TEMPLATE_ID || !EMAILJS_PUBLIC_KEY) {
        throw new Error('EmailJS credentials are not configured.');
      }
      await emailjs.send(EMAILJS_SERVICE_ID, EMAILJS_TEMPLATE_ID, templateParams, EMAILJS_PUBLIC_KEY);
      setStatus('success');
      setData(INITIAL_FORM);
    } catch (err) {
      console.error('Contact form send failed:', err);
      const fallbackSubject = templateParams.subject;
      const fallbackBody = `Name: ${data.name}\nEmail: ${data.email}\nCompany: ${data.company}\nInterested in: ${templateParams.interest}\n\n${data.message}`;
      window.location.href = `mailto:admin@orzur.ai?subject=${encodeURIComponent(fallbackSubject)}&body=${encodeURIComponent(fallbackBody)}`;
      setStatus('error');
      setErrorMsg("Couldn't send automatically - your mail client has opened with the details. Or email us directly at admin@orzur.ai.");
    }
  };

  if (status === 'success') {
    return (
      <div className="contact-form contact-success">
        <div className="contact-success-icon" aria-hidden="true">✓</div>
        <h3>Message sent</h3>
        <p>Thanks for reaching out. Our team will respond within one business day at the email you provided.</p>
        <button type="button" className="form-submit" onClick={() => setStatus('idle')}>
          Send another message
        </button>
      </div>
    );
  }

  return (
    <form className="contact-form" onSubmit={onSubmit} noValidate={false}>
      <h3>Send us a message</h3>

      <div className="form-group">
        <label htmlFor="cf-name">Name <span className="form-required">*</span></label>
        <input id="cf-name" name="name" type="text" placeholder="Your full name"
          value={data.name} onChange={update('name')} required disabled={status === 'sending'} />
      </div>
      <div className="form-group">
        <label htmlFor="cf-email">Email <span className="form-required">*</span></label>
        <input id="cf-email" name="email" type="email" placeholder="you@company.com"
          value={data.email} onChange={update('email')} required disabled={status === 'sending'} />
      </div>
      <div className="form-group">
        <label htmlFor="cf-company">Company <span className="form-required">*</span></label>
        <input id="cf-company" name="company" type="text" placeholder="Your company name"
          value={data.company} onChange={update('company')} required disabled={status === 'sending'} />
      </div>
      <div className="form-group">
        <label htmlFor="cf-interest">I'm interested in</label>
        <select id="cf-interest" name="interest"
          value={data.interest} onChange={update('interest')} disabled={status === 'sending'}>
          <option value="">Select one...</option>
          <option value="Booking a demo">Booking a demo</option>
          <option value="Starting a 90-day pilot">Starting a 90-day pilot</option>
          <option value="Pricing information">Pricing information</option>
          <option value="Partnership opportunities">Partnership opportunities</option>
          <option value="Something else">Something else</option>
        </select>
      </div>
      <div className="form-group">
        <label htmlFor="cf-message">Message</label>
        <textarea id="cf-message" name="message"
          placeholder="Tell us about your manufacturing operations and what you're looking for..."
          value={data.message} onChange={update('message')} disabled={status === 'sending'} />
      </div>

      {status === 'error' && errorMsg && (
        <div className="form-error">{errorMsg}</div>
      )}

      <button type="submit" className="form-submit" disabled={status === 'sending'}>
        {status === 'sending' ? 'Sending…' : 'Send Message'}
      </button>
    </form>
  );
}

function Contact() {
  return (
    <section className="contact" id="contact">
      <div className="section-inner">
        <div className="section-eyebrow">Contact Us</div>
        <div className="section-title">Ready to see what your data has been trying to tell you?</div>
        <div className="section-subtitle">
          Whether you're exploring how Orzur fits your operations or ready to start a 90-day pilot - we'd like to hear from you.
        </div>
        <div className="contact-layout">
          <div className="contact-side">
            <div className="contact-promise">
              <div className="contact-promise-label">Response Time</div>
              <div className="contact-promise-value">Within 1 business day</div>
            </div>

            <div className="contact-expect">
              <h4>What happens next</h4>
              <ul>
                <li>
                  <span className="contact-expect-num">1</span>
                  <div>
                    <strong>We get in touch</strong>
                    A member of our team reaches out within one business day to understand your setup.
                  </div>
                </li>
                <li>
                  <span className="contact-expect-num">2</span>
                  <div>
                    <strong>Tailored walkthrough</strong>
                    We schedule a 30-minute demo focused on your systems and questions - not a generic pitch.
                  </div>
                </li>
                <li>
                  <span className="contact-expect-num">3</span>
                  <div>
                    <strong>90-day pilot</strong>
                    If it's a fit, we scope a free pilot on one production line. Zero risk, no commitment.
                  </div>
                </li>
              </ul>
            </div>

            <div className="contact-direct">
              <div className="contact-direct-label">Prefer email?</div>
              <a href="mailto:admin@orzur.ai?subject=Orzur%20Demo%20Request" className="contact-direct-email">
                admin@orzur.ai
              </a>
              <div className="contact-direct-note">Book a demo or ask anything - we read every message.</div>
            </div>
          </div>
          <ContactForm />
        </div>
      </div>
    </section>
  );
}

function CTASection() {
  return (
    <section className="cta-section" id="demo">
      <div className="cta-inner">
        <div className="cta-headline">
          Orzur makes manufacturing leaders <span className="cta-highlight">superhuman.</span>
        </div>
        <div className="cta-sub">
          Start with a free 90-day pilot. One production line. Zero risk. See what your data has been trying to tell you.
        </div>
        <div className="cta-cards">
          {CTA_CARDS.map((c) => (
            <div key={c.title} className="cta-card">
              <h3>{c.title}</h3>
              <p>{c.body}</p>
            </div>
          ))}
        </div>
        <div className="cta-btn-row">
          <a href="#contact" className="btn-primary">Get a Demo <span>&rarr;</span></a>
        </div>
        <div className="cta-bottom">
          <span className="cta-bottom-tagline">From scattered data to trusted decisions.</span>
          <span className="cta-bottom-pillars">ASK &middot; ANALYZE &middot; DECIDE &middot; TRUST</span>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer>
      <div className="footer-inner">
        <div className="footer-top">
          <div className="footer-brand">
            <img src={LOGO_SRC} alt="Orzur" className="nav-logo-img" />
            <p>The Manufacturing Productivity Platform. From scattered data to trusted decisions.</p>
          </div>
          <div className="footer-col">
            <h4>Platform</h4>
            <a href="#platform">How It Works</a>
            <a href="#use-cases">Use Cases</a>
            <a href="#pillars">Four Pillars</a>
            <a href="#integrations">Integrations</a>
          </div>
          <div className="footer-col">
            <h4>Company</h4>
            <a href="#about">About</a>
            <a href="#careers">Careers</a>
            <a href="#contact">Contact Us</a>
          </div>
          <div className="footer-col">
            <h4>Resources</h4>
            <a href="#security">Documentation</a>
            <a href="#security">Security</a>
            <a href="#security">Trust Center</a>
          </div>
        </div>
        <div className="footer-bottom">
          <span className="footer-copy">© 2026 Orzur. All rights reserved.</span>
          <div className="footer-legal">
            <a href="#security">Privacy Policy</a>
            <a href="#security">Terms of Use</a>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default function App() {
  return (
    <div className="orzur-home">
      <Nav />
      <Hero />
      <Manifesto />
      <Problem />
      <Cost />
      <Rebuild />
      <UseCasesSection />
      <Pillars />
      <Integrations />
      <Testimonials />
      <Outcomes />
      <Why />
      <About />
      <Security />
      <Careers />
      <Contact />
      <CTASection />
      <Footer />
    </div>
  );
}
